import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  AppBar,
  Toolbar,
  Chip,
  LinearProgress,
  Divider,
  Stack,
  Alert,
} from '@mui/material';
import { CheckCircle, CalendarToday, ErrorOutline } from '@mui/icons-material';
import PaymentForm from '../components/payment/PaymentForm';
import PaymentStatus from '../components/payment/PaymentStatus';
import Logo from '../components/common/Logo';
import GoogleReviewBadge from '../components/common/GoogleReviewBadge';
import { PaymentLink, PaymentFormData } from '../types/PaymentLink';
import {
  getPaymentLinkByLinkId,
  markPaymentLinkAsVisited,
  processPayment,
  subscribeToPaymentLinkByLinkId,
} from '../services/paymentLinkService';
import { formatCurrency, formatDateTime } from '../utils/validators';
import { toast } from 'react-toastify';
import { PAYMENT_CONFIG } from '../config/payment'; // NEW - Feature flag for real payment

const PaymentPage: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const [paymentLink, setPaymentLink] = useState<PaymentLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'failed'>(
    'idle'
  );
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (linkId) {
      loadPaymentLink();
    }
  }, [linkId]);

  // Subscribe to realtime updates for this payment link
  // This ensures the page auto-updates when backend processes a payment callback
  useEffect(() => {
    if (!linkId) return;

    console.log('[PaymentPage] Setting up realtime listener for:', linkId);
    const unsubscribe = subscribeToPaymentLinkByLinkId(linkId, (updatedLink) => {
      if (updatedLink) {
        console.log('[PaymentPage] Realtime update received:', {
          totalPaid: updatedLink.totalPaid,
          remainingAmount: updatedLink.remainingAmount,
          invoicePaid: updatedLink.invoicePaid,
        });
        setPaymentLink(updatedLink);
        
        // Auto-update status if fully paid
        if (updatedLink.invoicePaid && paymentStatus !== 'success') {
          setPaymentStatus('success');
          toast.success('Payment confirmed! Invoice fully paid.');
        }
      }
    });

    return () => {
      console.log('[PaymentPage] Cleaning up realtime listener');
      unsubscribe();
    };
  }, [linkId]);

  const loadPaymentLink = async () => {
    try {
      setLoading(true);
      const link = await getPaymentLinkByLinkId(linkId!);

      if (!link) {
        setNotFound(true);
        return;
      }

      // Debug logging
      console.log('PaymentPage - Payment link loaded:', {
        linkId: link.linkId,
        type: link.type,
        allowPartialPayment: link.allowPartialPayment,
        depositPaid: link.depositPaid,
        remainingAmount: link.remainingAmount,
        invoiceAmount: link.invoiceAmount
      });

      // Check if expired - REMOVED: Allow payment even if expired
      // if (isPaymentLinkExpired(link)) {
      //   setExpired(true);
      //   setPaymentLink(link);
      //   return;
      // }

      // Check if already fully paid
      if (link.invoicePaid) {
        setPaymentStatus('success');
        setPaymentLink(link);
        return;
      }

      // Mark as visited
      await markPaymentLinkAsVisited(link.id);

      // Reload the link to get updated visit count
      const updatedLink = await getPaymentLinkByLinkId(linkId!);
      setPaymentLink(updatedLink);
    } catch (error) {
      console.error('Error loading payment link:', error);
      toast.error('Failed to load payment information');
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (paymentData: PaymentFormData) => {
    if (!paymentLink) return;

    if (PAYMENT_CONFIG.useRealPayment) {
      console.warn('[PaymentPage] handlePaymentSubmit called while real payments enabled.');
      toast.info('Redirecting to bank for secure payment. Please complete the 3D Secure step.');
      return;
    }

    try {
      setPaymentStatus('processing');
      setErrorMessage('');

      // Use the processPayment service which handles partial payments
      await processPayment(paymentLink.id, paymentData.amount, paymentData.cardNumber.slice(-4), 'visa');

      // Reload payment link to get updated data
      const updatedLink = await getPaymentLinkByLinkId(linkId!);
      setPaymentLink(updatedLink);

      // Check if fully paid
      if (updatedLink && updatedLink.invoicePaid) {
        setPaymentStatus('success');
        toast.success('Payment successful! Invoice fully paid.');
      } else {
        setPaymentStatus('idle');
        toast.success(`Payment of ${formatCurrency(paymentData.amount, paymentLink.currency)} successful!`);
      }
    } catch (error: any) {
      console.error('Error processing payment:', error);
      setPaymentStatus('failed');
      setErrorMessage(error.message || 'An unexpected error occurred. Please try again.');
      toast.error(error.message || 'Payment failed');
    }
  };

  const handleRetry = () => {
    setPaymentStatus('idle');
    setErrorMessage('');
    loadPaymentLink();
  };

  // Loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Not found state
  if (notFound) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
        }}
      >
        <Card sx={{ maxWidth: 500, textAlign: 'center', p: 4 }}>
          <CardContent>
            <ErrorOutline sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Payment Link Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              The payment link you are looking for does not exist or has been removed.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Expired state - REMOVED: Allow payment even if due date passed
  // No longer showing expired message

  if (!paymentLink) return null;

  // Calculate progress percentage
  const progressPercentage = (paymentLink.totalPaid / paymentLink.invoiceAmount) * 100;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
        backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%232e7d32" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        pb: 4,
      }}
    >
      {/* Header */}
      <AppBar
        position="static"
        elevation={2}
        sx={{
          backgroundColor: '#f5f5f5',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar sx={{ justifyContent: 'center' }}>
          <Logo size="medium" />
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {paymentStatus === 'idle' || paymentStatus === 'processing' ? (
          <Grid container spacing={3}>
            {/* Invoice Summary - Full Width */}
            <Grid item xs={12}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                  boxShadow: 3,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 700 }}>
                    Invoice Summary
                  </Typography>

                  {paymentLink.invoiceNumber && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Invoice #: {paymentLink.invoiceNumber}
                    </Typography>
                  )}

                  {/* Split Payment Context */}
                  {paymentLink.splitParentId && (
                    <Alert
                      severity="info"
                      sx={{
                        mt: 2,
                        mb: 2,
                        backgroundColor: '#e3f2fd !important',
                        borderLeft: '4px solid #2196f3',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Split Payment
                      </Typography>
                      <Typography variant="caption" display="block">
                        Payer: <strong>{paymentLink.splitPayerName || paymentLink.customerName}</strong>
                      </Typography>
                      <Typography variant="caption" display="block">
                        Your Share: <strong>{formatCurrency(paymentLink.splitAssignedAmount || paymentLink.invoiceAmount, paymentLink.currency)}</strong>
                      </Typography>
                      {paymentLink.allowPartialPayment && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                          You can pay your share in multiple installments.
                        </Typography>
                      )}
                    </Alert>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Description */}
                  {paymentLink.description && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Note:</strong> {paymentLink.description}
                      </Typography>
                    </Alert>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Total Invoice Amount
                        </Typography>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                          {formatCurrency(paymentLink.invoiceAmount, paymentLink.currency)}
                        </Typography>
                      </Box>
                    </Grid>

                    {paymentLink.depositAmount > 0 && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Required Deposit
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {formatCurrency(paymentLink.depositAmount, paymentLink.currency)}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Total Paid So Far
                        </Typography>
                        <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                          {formatCurrency(paymentLink.totalPaid, paymentLink.currency)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Remaining Balance
                        </Typography>
                        <Typography variant="h6" color="error.main" sx={{ fontWeight: 700 }}>
                          {formatCurrency(paymentLink.remainingAmount, paymentLink.currency)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Progress Bar */}
                  <Box sx={{ mt: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        Payment Progress
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {progressPercentage.toFixed(0)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(progressPercentage, 100)}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          background: 'linear-gradient(90deg, #4caf50 0%, #2e7d32 100%)',
                        },
                      }}
                    />
                  </Box>

                  {/* Deposit Status */}
                  {paymentLink.depositAmount > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {paymentLink.depositPaid ? (
                        <Chip
                          icon={<CheckCircle />}
                          label={`Deposit Paid (${formatCurrency(paymentLink.depositAmount, paymentLink.currency)})`}
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          label={`Deposit Required: ${formatCurrency(paymentLink.depositAmount, paymentLink.currency)}`}
                          color="warning"
                          size="small"
                        />
                      )}

                      {paymentLink.depositPaid && !paymentLink.invoicePaid && (
                        <Chip
                          label="Partial Payment"
                          color="info"
                          size="small"
                        />
                      )}
                    </Box>
                  )}

                  {/* Due Dates */}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                    {paymentLink.depositDueDate && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          Deposit Due: {formatDateTime(paymentLink.depositDueDate.toDate())}
                        </Typography>
                      </Box>
                    )}
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Invoice Due: {formatDateTime(paymentLink.invoiceDueDate.toDate())}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Payment History - If there are payments */}
            {paymentLink.payments && paymentLink.payments.length > 0 && (
              <Grid item xs={12}>
                <Card sx={{ boxShadow: 2 }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 700 }}>
                      Payment History
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Stack spacing={2}>
                      {paymentLink.payments.map((payment) => (
                        <Box
                          key={payment.id}
                          sx={{
                            p: 2,
                            backgroundColor: payment.status === 'success' ? '#e8f5e9' : '#ffebee',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: payment.status === 'success' ? '#4caf50' : '#f44336',
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6}>
                              <Box display="flex" alignItems="center" gap={1}>
                                {payment.status === 'success' && (
                                  <CheckCircle fontSize="small" color="success" />
                                )}
                                <Typography variant="body1" fontWeight={600}>
                                  {formatCurrency(payment.amount, payment.currency)}
                                </Typography>
                                <Chip
                                  label={payment.paymentType === 'deposit' ? 'Deposit' : payment.paymentType === 'full' ? 'Full Payment' : 'Partial Payment'}
                                  size="small"
                                  color={payment.paymentType === 'deposit' ? 'warning' : payment.paymentType === 'full' ? 'success' : 'info'}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDateTime(payment.timestamp.toDate())}
                                {payment.cardLastFour && ` - Card ****${payment.cardLastFour}`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Stack>

                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Total Paid: {formatCurrency(paymentLink.totalPaid, paymentLink.currency)} / {formatCurrency(paymentLink.invoiceAmount, paymentLink.currency)}
                      </Typography>
                      <Typography variant="body2" color="error.main" fontWeight={600}>
                        Remaining: {formatCurrency(paymentLink.remainingAmount, paymentLink.currency)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Payment Form */}
            <Grid item xs={12}>
              <Card sx={{ boxShadow: 3 }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 700 }}>
                    Make a Payment
                  </Typography>

                  {/* Payment Instructions */}
                  {!paymentLink.depositPaid && (
                    <Alert
                      severity="info"
                      sx={{
                        mb: 2,
                        backgroundColor: '#ffffff !important',
                        color: '#000000 !important',
                        borderLeft: '4px solid !important',
                        borderLeftColor: '#2196f3 !important',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15) !important',
                        fontWeight: '600 !important',
                        fontSize: '14px !important',
                        '& .MuiAlert-icon': {
                          color: '#2196f3 !important',
                        },
                        '& .MuiAlert-message': {
                          color: '#000000 !important',
                          fontWeight: '600 !important',
                        },
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Minimum Payment Required:</strong> {formatCurrency(paymentLink.depositAmount, paymentLink.currency)} (Deposit)
                      </Typography>
                      {paymentLink.allowPartialPayment && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          You can pay more than the deposit if you wish.
                        </Typography>
                      )}
                    </Alert>
                  )}

                  {paymentLink.depositAmount > 0 && paymentLink.depositPaid && !paymentLink.invoicePaid && (
                    <Alert
                      severity="success"
                      sx={{
                        mb: 2,
                        backgroundColor: '#ffffff !important',
                        color: '#000000 !important',
                        borderLeft: '4px solid !important',
                        borderLeftColor: '#4caf50 !important',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15) !important',
                        fontWeight: '600 !important',
                        fontSize: '14px !important',
                        '& .MuiAlert-icon': {
                          color: '#4caf50 !important',
                        },
                        '& .MuiAlert-message': {
                          color: '#000000 !important',
                          fontWeight: '600 !important',
                        },
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Deposit Paid</strong> - Thank you! You can now pay the remaining balance.
                      </Typography>
                      {paymentLink.allowPartialPayment && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          You can pay any amount towards the remaining balance.
                        </Typography>
                      )}
                    </Alert>
                  )}

                  <PaymentForm
                    amount={paymentLink.remainingAmount}
                    onSubmit={handlePaymentSubmit}
                    disabled={paymentStatus === 'processing'}
                    allowPartialPayment={paymentLink.allowPartialPayment}
                    depositAmount={paymentLink.depositAmount}
                    depositPaid={paymentLink.depositPaid}
                    remainingAmount={paymentLink.remainingAmount}
                    currency={paymentLink.currency}
                    useRealPayment={PAYMENT_CONFIG.useRealPayment}
                    paymentLinkId={paymentLink.id}
                    linkId={paymentLink.linkId}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <PaymentStatus
            status={paymentStatus}
            amount={paymentStatus === 'success' ? paymentLink.totalPaid : undefined}
            currency={paymentStatus === 'success' ? paymentLink.currency : undefined}
            errorMessage={errorMessage}
            onRetry={paymentStatus === 'failed' ? handleRetry : undefined}
          />
        )}
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 4,
          textAlign: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Google Review Badge */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <GoogleReviewBadge variant="compact" />
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          © {new Date().getFullYear()} Alexa Travel Agency. All rights reserved.
        </Typography>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <Box
            component="img"
            src="/assets/visit-antalya-2.svg"
            alt="Visit Antalya"
            sx={{
              maxWidth: { xs: 150, sm: 200 },
              height: 'auto',
              opacity: 0.8,
            }}
            onError={(e: any) => {
              // Hide image if not found
              e.target.style.display = 'none';
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentPage;
